/**
 * Product Groups tab — Settings → Product & Services.
 * Columns: Code | Name | Description | Status (Active/Hidden) | Created At
 * Matches legacy ProductGroupsTab column set exactly.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Boxes } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { formatDate } from '@/lib/format';
import { useAdminProductGroups, type ProductGroupRow } from '@/api/products-admin';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const COLUMNS: ColumnDef<ProductGroupRow, unknown>[] = [
  {
    id: 'product_group_code',
    header: 'Code',
    accessorKey: 'product_group_code',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.product_group_code}
      </span>
    ),
  },
  {
    id: 'product_group_name',
    header: 'Name',
    accessorKey: 'product_group_name',
    cell: ({ row }) => (
      <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
        {row.original.product_group_name}
      </span>
    ),
  },
  {
    id: 'product_group_description',
    header: 'Description',
    accessorKey: 'product_group_description',
    cell: ({ row }) => row.original.product_group_description || '—',
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.product_group_hidden ? (
        <StatusPill tone="warning">Hidden</StatusPill>
      ) : (
        <StatusPill tone="success">Active</StatusPill>
      ),
  },
  {
    id: 'product_group_created_at',
    header: 'Created',
    cell: ({ row }) => formatDate(row.original.product_group_created_at),
  },
];

export function ProductGroupsTab() {
  const { data: allGroups = [], isLoading, isError, refetch } = useAdminProductGroups();
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 300);

  const rows = useMemo(() => {
    if (!query) return allGroups;
    const q = query.toLowerCase();
    return allGroups.filter(
      (g) =>
        g.product_group_name.toLowerCase().includes(q) ||
        g.product_group_code.toLowerCase().includes(q),
    );
  }, [allGroups, query]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Search groups…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load product groups"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={rows}
          getRowId={(row) => String(row.product_group_id)}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="No product groups found"
              description="Try adjusting your search."
            />
          }
        />
      )}
    </div>
  );
}
